import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { DashboardEntity, type DashboardPage } from './dashboard.entity';
import { PortalSiteEntity } from './portal-site.entity';

/**
 * Portal-site assignment for a `STOREFRONT`-scope dashboard. Many-to-
 * many at the table level: a single dashboard design can serve many
 * portal sites (a tenant with several brand portals can share one
 * design across all of them) and a portal site has at most one
 * dashboard assignment PER PAGE (UNIQUE on `(_fk_portal_site__id,
 * page)`) — one home design and one account design per portal.
 *
 * Resolution at storefront load time, for a given `page`:
 *   1. Find a row where `_fk_portal_site__id == active portal` AND the
 *      bound design's `page` matches.
 *   2. If none, find a STOREFRONT design of that `page` with no
 *      `dashboard_portal_site` rows (the tenant-default fallback).
 *   3. If none, render no dashboard.
 *
 * Empty for CRM-scope dashboards.
 */
@Index('idx_dashboard_portal_site_dashboard', ['dashboardId'])
@Index('idx_dashboard_portal_site_portal_page', ['portalSiteId', 'page'], {
	unique: true,
})
@Entity('dashboard_portal_site')
export class DashboardPortalSiteEntity extends BaseEntity {
	@Column('int', { name: '_fk_dashboard__id', unsigned: true })
	public dashboardId!: number;

	@Column('int', { name: '_fk_portal_site__id', unsigned: true })
	public portalSiteId!: number;

	/**
	 * Denormalized copy of the bound design's `page` (see
	 * `DashboardEntity.page`). Stored here so the "one design per portal
	 * per page" rule can be a DB-level UNIQUE invariant — a portal can be
	 * bound to one home design and one account design, but not two of the
	 * same page. Set by the binding service from the design's `page` when
	 * links are written. Defaults to 'home' (all pre-existing bindings are
	 * home designs).
	 */
	@Column('varchar', { name: 'page', length: 16, default: () => "'home'" })
	public page!: DashboardPage;

	@ManyToOne(() => DashboardEntity, (dashboard) => dashboard.portalSiteLinks, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_dashboard__id', referencedColumnName: 'id' }])
	public dashboard!: DashboardEntity;

	@ManyToOne(() => PortalSiteEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_portal_site__id', referencedColumnName: 'id' }])
	public portalSite!: PortalSiteEntity;
}
